import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { initializeIntercom, startSurvey } from "@/utils/intercom";
import { SURVEY_IDS } from "@/constants/intercom";
import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Divider,
  Flex,
  Image,
  Indicator,
  Popover,
  Text as MantineText,
  Tooltip,
} from "@mantine/core";

import {
  IconBell,
  IconChevronDown,
  IconLogout,
  IconQuestionMark,
  IconSettings,
  IconShare3,
  IconX,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import SmartWizLogo from "../../Logos/SmartWizLogo";
import "./ClientModal.scss";
import NotificationDrawer from "../../NotificationDrawer/NotificationDrawer";
import "./Header.scss";
import { getTaxProInfo } from "@utils/api.ts";
import { AppResources, AppRoutes, ifSkipAuth } from "@/constants";
import useLogo from "@/utils/hooks/useLogo";
import NavCredits from "@/pages/Credits/components/NavCredits";
import { useBalance } from "@/pages/Credits/hooks/useBalance";
import ClientModal from "./ClientModal";
import { useNotificationStore } from "@/components/NotificationDrawer/store/notificationStore";
import { showNotification } from "@mantine/notifications";
import { useAuthStore } from "@/utils/stores/authStore";
import RolePackageBadges from "@/components/RolePackageBadges/RolePackageBadges";

const Header: React.FC = () => {
  const { hasNewNotifications } = useNotificationStore();
  const { user, role, businessInfo } = useAuthStore();
  const [isIntercomInitialized, setIsIntercomInitialized] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [opened, { open, close }] = useDisclosure(false);
  const [openedNotif, { open: openNotif, close: closeNotif }] =
    useDisclosure(false);
  const fallbackLogo = AppResources.createAccount;

  const isPublicPage = ifSkipAuth(location.pathname);
  const isRegisterPage = location.pathname === AppRoutes.register;
  const isLoginPage = location.pathname === AppRoutes.login;

  const queryParams = new URLSearchParams(location.search);
  const businessId = queryParams.get("businessId");
  const registerPageHasParams = businessId !== null;

  // Use business info from auth store, or fall back to localStorage for register page
  const [localBusinessName, setLocalBusinessName] = useState("");
  const businessName = businessInfo?.businessName || localBusinessName;
  const packageName = businessInfo?.packageName || "";

  const { logoUrl, handleLogoError } = useLogo({
    businessId,
    fallbackImage: fallbackLogo,
  });

  const {
    data: creditResponse,
    isError: isBalanceError,
    error: balanceError,
  } = useBalance(!isPublicPage && role !== "client");

  const fetchBusinessInfo: () => Promise<void> = async () => {
    const response = await getTaxProInfo(businessId);
    if (response?.data.content.businessName) {
      setLocalBusinessName(response.data.content.businessName);
    }
  };

  useEffect(() => {
    if (isBalanceError && !isPublicPage) {
      showNotification({
        title: "Error",
        message: `${balanceError}`,
        color: "red",
        icon: <IconX size="1.1rem" />,
      });
    }

    // Only fetch business info for register page with business ID
    if (registerPageHasParams && isRegisterPage) {
      fetchBusinessInfo();
    }
  }, [
    location.pathname,
    location.search,
    isBalanceError,
    balanceError,
    isPublicPage,
    registerPageHasParams,
    isRegisterPage,
  ]);

  useEffect(() => {
    if (user && !isIntercomInitialized) {
      initializeIntercom().then((success) => {
        if (success) {
          setIsIntercomInitialized(true);
        }
      });
    }
  }, [user, role, isIntercomInitialized]);

  const openSettings: () => void = () => {
    navigate(AppRoutes.settings);
  };

  const openHome: () => void = () => {
    navigate(AppRoutes.home);
  };

  const logoutUser: () => void = () => {
    navigate(AppRoutes.logout);
  };

  const handleFeedbackClick: () => void = () => {
    startSurvey(SURVEY_IDS.FEEDBACK);
  };

  return (
    <>
      {isPublicPage ? (
        <header
          className="header"
          style={{ marginTop: isPublicPage ? "3rem" : "5rem" }}
        >
          {localStorage.getItem("businessInfo") && !isLoginPage && (
            <>
              {registerPageHasParams ? (
                <Image
                  className="logo-black"
                  src={logoUrl}
                  style={{ width: "7.3rem" }}
                  alt="Business Logo"
                  onError={handleLogoError} // Use fallback if the image is broken
                />
              ) : (
                <div>
                  <SmartWizLogo isSmall />
                </div>
              )}
            </>
          )}
        </header>
      ) : (
        <header className="header-container">
          <NotificationDrawer opened={openedNotif} close={closeNotif} />
          <span className="header-left">
            <Flex direction="column" gap={4} align="flex-start">
              <SmartWizLogo isSmall />
              <RolePackageBadges size="xs" gap="xs" />
            </Flex>
          </span>
          <span className="header-right">
            {role !== "client" && (
              <Button size="md" radius="md" onClick={open}>
                <Flex align="center" gap={4}>
                  <MantineText
                    size="md"
                    fw={700}
                    c="white"
                    className="invite-button-text"
                  >
                    <span className="desktop-text">Invite Clients</span>
                    <span className="mobile-text">Invite</span>
                  </MantineText>
                  <IconShare3 size={18} stroke={2} />
                </Flex>
              </Button>
            )}
            <Tooltip label="Send feedback" withArrow>
              <ActionIcon
                variant="transparent"
                radius="xl"
                style={{ border: "2px solid #e0e0e0" }}
                color="gray"
                onClick={handleFeedbackClick}
              >
                <IconQuestionMark size={18} />
              </ActionIcon>
            </Tooltip>
            {!isPublicPage && role !== "client" && (
              <NavCredits
                credits={
                  creditResponse?.allocatedCredits ??
                  creditResponse?.businessBalance ??
                  0
                }
                hasRequestedCredits={creditResponse?.creditRequest ?? false}
              />
            )}
            <button className="notification-button" onClick={openNotif}>
              <Indicator
                inline
                disabled={!hasNewNotifications}
                color="red"
                size={14}
                offset={5}
                withBorder
              >
                <IconBell size={30} />
              </Indicator>
            </button>
            <Popover
              offset={{ crossAxis: -100 }}
              position="bottom"
              withArrow
              shadow="md"
            >
              <Popover.Target>
                <div className="profile-button">
                  {role !== "client" && (
                    <Avatar
                      p={4}
                      src={
                        businessInfo?.logoUrl ||
                        sessionStorage.getItem("logoUrl")
                      }
                      radius="xl"
                      size="lg"
                      color="blue"
                    />
                  )}
                  <MantineText truncate="end" maw={225}>
                    {role !== "client" ? businessName : user?.email}
                  </MantineText>
                  <IconChevronDown size={15} />
                </div>
              </Popover.Target>
              <Popover.Dropdown w={300}>
                <div>
                  <div className="dropdown-header">
                    <Button
                      variant="transparent"
                      size="md"
                      w="100%"
                      onClick={openHome}
                      leftSection={
                        role !== "client" ? (
                          <Avatar
                            p={4}
                            src={
                              businessInfo?.logoUrl ||
                              sessionStorage.getItem("logoUrl")
                            }
                            radius="xl"
                            size="md"
                            color="blue"
                          />
                        ) : undefined
                      }
                    >
                      <div>
                        {role !== "client" && (
                          <MantineText truncate="end" maw={175}>
                            {businessName}
                          </MantineText>
                        )}
                        <MantineText
                          truncate="end"
                          maw={175}
                          size="xs"
                          c="dimmed"
                        >
                          {user?.email || ""}
                        </MantineText>
                      </div>
                    </Button>
                  </div>
                  {packageName && role !== "client" && (
                    <Badge
                      size="lg"
                      variant="outline"
                      color="blue"
                      fullWidth
                      style={{ marginBottom: "1rem" }}
                    >
                      {packageName} Plan
                    </Badge>
                  )}

                  <Divider />
                  <div className="dropdown-item">
                    <Button
                      variant="transparent"
                      onClick={openSettings}
                      leftSection={<IconSettings size={20} />}
                      w="100%"
                      className="dropdown-item"
                    >
                      <MantineText>Settings</MantineText>
                    </Button>
                  </div>

                  <Divider />

                  <div className="dropdown-item">
                    <Button
                      variant="transparent"
                      onClick={logoutUser}
                      w="100%"
                      leftSection={<IconLogout size={20} color="red" />}
                      className="dropdown-item"
                    >
                      <MantineText c="red">Logout</MantineText>
                    </Button>
                  </div>
                </div>
              </Popover.Dropdown>
            </Popover>
          </span>
          <ClientModal opened={opened} onClose={close} />
        </header>
      )}
    </>
  );
};

export default Header;
